/**
 * Kontrak sub menu "Log Percakapan" (v2/M6) — rancangan
 * `docs-local/plans/ai-assistant/08-log-percakapan.md`. Dipakai server
 * (`/api/admin/ai-assistant/log/*`) dan tiga lapis UI di
 * `/admin/ai-assistant/log`.
 *
 * BERBEDA dari `ai-assistant-usage-log.ts`: log ini SENGAJA memuat identitas
 * pengguna (lapis 1–2) dan isi percakapan (lapis 3), karena tujuannya melacak
 * "siapa bertanya apa, dijawab model mana". Karena itu lapis 3 dijaga izin
 * terpisah dan setiap pembukaannya dicatat ke ActivityLog — lihat §6 rancangan.
 * Label vendor tetap diturunkan lewat `vendorOfMessage` dari kontrak lama,
 * supaya lencana di dua halaman tidak mungkin berbeda.
 */

import type { UsageLogVendor } from "./ai-assistant-usage-log";

/**
 * Kunci `RolePermission` untuk izin membaca ISI transkrip (rancangan 08 §6.1).
 * Ada di file tipe (bukan di modul server) supaya halaman Role & Permission
 * bisa memakainya tanpa menarik Prisma ke bundle browser.
 */
export const ASSISTANT_TRANSCRIPT_FEATURE = "read-assistant-transcript";

/** Modalitas giliran; sama dengan `AssistantMessage.modality`. */
export const ASSISTANT_LOG_MODALITIES = ["text", "voice"] as const;
export type AssistantLogModality = (typeof ASSISTANT_LOG_MODALITIES)[number];

/**
 * Status giliran di transkrip. Berbeda dari log penyedia yang hanya ok/error:
 * di transkrip `limited` penting karena pertanyaan itu ditolak plafon dan
 * tidak pernah sampai ke penyedia (tidak menghabiskan token).
 */
export const ASSISTANT_LOG_STATUSES = ["ok", "error", "limited"] as const;
export type AssistantLogStatus = (typeof ASSISTANT_LOG_STATUSES)[number];

/** Rentang hari kalender WITA yang boleh diminta; menjepit biaya query. */
export const ASSISTANT_LOG_DEFAULT_DAYS = 30;
export const ASSISTANT_LOG_MAX_DAYS = 90;

/** Paginasi wajib di tiap endpoint list. */
export const ASSISTANT_LOG_DEFAULT_LIMIT = 25;
export const ASSISTANT_LOG_MAX_LIMIT = 100;

/**
 * Batas atas pencarian nama/email: kata kunci diterjemahkan dulu menjadi
 * daftar id user, dan daftar itu dijepit supaya `WHERE userId IN (...)` tidak
 * tumbuh tanpa batas.
 */
export const ASSISTANT_LOG_SEARCH_MAX_USERS = 200;

/**
 * Batas pemindaian `toolsUsed` satu percakapan untuk panel "Sumber data
 * dipakai". `toolsUsed` adalah kolom array sehingga tidak bisa di-`groupBy` di
 * DB; pemindaiannya dijepit agar percakapan panjang tidak memuat semuanya.
 */
export const ASSISTANT_LOG_TOOLS_SCAN_MAX = 500;

/** Jumlah giliran satu model — dipakai badge "model dominan" & panel "Model yang dipakai". */
export interface AssistantLogModelUseDto {
	/** null = model tidak diketahui (giliran sebelum v2/M5). */
	model: string | null;
	vendor: UsageLogVendor;
	turns: number;
}

/** Estimasi biaya; `usd` null = tarif model yang dipakai belum diisi admin. */
export interface AssistantLogCostDto {
	usd: number | null;
	/** true bila ada model berpemakaian yang tarifnya belum diisi, jadi angkanya kurang. */
	incomplete: boolean;
}

/** Angka ringkas yang dipakai lapis 1 (per baris) dan lapis 2 (per user). */
export interface AssistantLogTotalsDto {
	conversations: number;
	/** Giliran yang benar-benar menyentuh penyedia. */
	turns: number;
	inputTokens: number;
	outputTokens: number;
	cost: AssistantLogCostDto;
	errors: number;
}

/** Satu baris tabel "Per Pengguna" (lapis 1). */
export interface AssistantLogUserRowDto extends AssistantLogTotalsDto {
	userId: string;
	/** null = nama belum diisi di profil; UI jatuh ke email. */
	name: string | null;
	email: string;
	/** Model terbanyak dipakai user ini; null = belum ada giliran dengan model dikenal. */
	dominantModel: AssistantLogModelUseDto | null;
	/** Giliran terakhir yang menyentuh penyedia (ISO UTC); null = tidak ada di rentang ini. */
	lastActiveAt: string | null;
}

/**
 * Ringkasan lima stat card lapis 1: seluruh rentang lintas pengguna, bukan
 * hanya halaman yang tampil. Ikut di respons `/log/users` supaya stat card
 * tidak perlu agregat kedua yang isinya sama.
 */
export interface AssistantLogOverviewDto extends AssistantLogTotalsDto {
	activeUsers: number;
}

/** Respons `GET /log/users` — berhalaman offset supaya footer bisa "1–N dari M". */
export interface AssistantLogUsersPageDto {
	rows: AssistantLogUserRowDto[];
	/** Jumlah pengguna yang cocok filter (bukan jumlah baris halaman ini). */
	total: number;
	offset: number;
	limit: number;
	rangeDays: number;
	/** Awal rentang (ISO) = 00:00 WITA hari pertama rentang. */
	since: string;
	overview: AssistantLogOverviewDto;
}

/** Kartu profil lapis 2. */
export interface AssistantLogUserProfileDto {
	userId: string;
	name: string | null;
	email: string;
	role: string | null;
	emailVerified: boolean;
	/** Role user ini masih punya izin `use-ai-assistant`. */
	canUseAssistant: boolean;
}

/** Satu kartu percakapan di lapis 2. */
export interface AssistantLogConversationCardDto {
	id: string;
	title: string;
	/** Pesan pertama percakapan (ISO UTC). */
	startedAt: string;
	/** Pesan terakhir (ISO UTC). */
	lastMessageAt: string;
	messages: number;
	/** Model yang menjawab; lebih dari satu bila sempat failover. */
	models: AssistantLogModelUseDto[];
	hasVoice: boolean;
	errors: number;
}

/** Respons `GET /log/users/:userId` — profil + ringkasan + halaman percakapan. */
export interface AssistantLogUserDetailDto {
	profile: AssistantLogUserProfileDto;
	totals: AssistantLogTotalsDto;
	conversations: AssistantLogConversationCardDto[];
	/** id percakapan terakhir halaman ini; null = halaman terakhir. */
	nextCursor: string | null;
	rangeDays: number;
	since: string;
}

/** Satu gelembung di transkrip (lapis 3). */
export interface AssistantLogMessageDto {
	id: string;
	role: "user" | "assistant";
	/** Waktu pesan (ISO UTC; UI menampilkannya dalam WITA). */
	at: string;
	/**
	 * Isi pesan, atau null bila disembunyikan: di luar rentang retensi, atau
	 * admin memilih mode "sembunyikan isi". Metadata di bawah ini tetap terkirim.
	 */
	content: string | null;
	status: AssistantLogStatus;
	modality: AssistantLogModality;
	vendor: UsageLogVendor;
	slot: string | null;
	model: string | null;
	latencyMs: number | null;
	inputTokens: number | null;
	outputTokens: number | null;
	toolsUsed: string[];
	/** Halaman tempat pengguna bertanya; null = tidak tercatat. */
	pageRoute: string | null;
}

/** Hitungan pemakaian satu tool di panel "Sumber data dipakai". */
export interface AssistantLogToolUseDto {
	name: string;
	uses: number;
}

/** Metadata percakapan untuk panel kanan lapis 3. */
export interface AssistantLogConversationMetaDto {
	id: string;
	title: string;
	startedAt: string;
	lastMessageAt: string;
	messages: number;
	turns: number;
	errors: number;
	modalities: AssistantLogModality[];
	/** Halaman asal yang muncul di percakapan ini (unik, urut kemunculan). */
	pageRoutes: string[];
	inputTokens: number;
	outputTokens: number;
	cost: AssistantLogCostDto;
	models: AssistantLogModelUseDto[];
	tools: AssistantLogToolUseDto[];
}

/** Respons `GET /log/conversations/:id` — metadata + satu halaman pesan (kursor mundur). */
export interface AssistantLogTranscriptDto {
	conversation: AssistantLogConversationMetaDto;
	/** Pemilik percakapan (lapis 3 memang menampilkan identitas). */
	user: { userId: string; name: string | null; email: string };
	/** Pesan halaman ini, urut lama → baru (seperti gelembung dibaca). */
	messages: AssistantLogMessageDto[];
	/**
	 * Kursor "muat pesan sebelumnya" = id pesan tertua di halaman ini;
	 * null = sudah sampai awal percakapan.
	 */
	prevCursor: string | null;
	/** Rentang retensi isi percakapan (hari). */
	retentionDays: number;
	/** true bila ada pesan di halaman ini yang isinya disembunyikan. */
	contentHidden: boolean;
}

/** Query `GET /log/users` (sudah divalidasi skema route). */
export interface AssistantLogUsersQuery {
	days?: number;
	vendor?: UsageLogVendor;
	status?: AssistantLogStatus;
	modality?: AssistantLogModality;
	/** Kata kunci nama atau email. */
	q?: string;
	limit?: number;
	offset?: number;
}

/** Query `GET /log/users/:userId`. */
export interface AssistantLogUserDetailQuery {
	days?: number;
	vendor?: UsageLogVendor;
	status?: AssistantLogStatus;
	modality?: AssistantLogModality;
	/** Kata kunci judul percakapan. */
	q?: string;
	limit?: number;
	cursor?: string;
}

/** Query `GET /log/conversations/:id`. */
export interface AssistantLogTranscriptQuery {
	limit?: number;
	/** id pesan tertua yang sudah dimuat; halaman berikutnya mundur dari situ. */
	cursor?: string;
	/** true = jangan kirim isi pesan, metadata saja (toggle "Sembunyikan isi"). */
	hideContent?: boolean;
}
